import React, { useEffect, useState, useCallback } from "react";
import { Search, Box, ExternalLink, Loader2, Check, Eye, EyeOff, Pencil, X, Save } from "lucide-react";
import client, { displayName } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PriceCell = ({ product, onSaved }) => {
  const [val, setVal] = useState(product.admin_price ?? "");
  const [saving, setSaving] = useState(false);
  const dirty = String(val) !== String(product.admin_price ?? "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/admin/products/${encodeURIComponent(product.sku)}`, { admin_price: val === "" ? null : parseFloat(val) });
      toast.success(`Price updated · ${product.sku}`);
      onSaved(data);
    } catch { toast.error("Failed to save price"); } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-steel-light">$</span>
        <Input type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && dirty && save()} placeholder="Quote" className="h-8 w-24 pl-5 mono text-xs" />
      </div>
      {dirty && <button onClick={save} disabled={saving} className="w-7 h-7 grid place-items-center rounded bg-orange text-white">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}</button>}
    </div>
  );
};

const VisibilityToggle = ({ product, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const visible = product.is_visible !== false;

  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/admin/products/${encodeURIComponent(product.sku)}`, { is_visible: !visible });
      toast.success(visible ? `${product.sku} — hidden from catalogue` : `${product.sku} — visible in catalogue`);
      onSaved(data);
    } catch { toast.error("Failed to update visibility"); } finally { setSaving(false); }
  };

  return (
    <button onClick={toggle} disabled={saving} title={visible ? "Click to hide from catalogue" : "Click to show in catalogue"} className={`p-1.5 rounded transition-colors ${visible ? "text-emerald-600 hover:bg-emerald-50" : "text-steel-light hover:bg-surface"}`}>
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
    </button>
  );
};

const EditModal = ({ product, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: product.name || "",
    short_description: product.short_description || "",
    description: product.description || "",
    brand: product.brand || "",
    is_in_stock: product.is_in_stock ?? true,
    stock_status: product.stock_status || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/admin/products/${encodeURIComponent(product.sku)}`, form);
      toast.success(`Product updated · ${product.sku}`);
      onSaved(data);
      onClose();
    } catch { toast.error("Failed to save product"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-bold text-graphite">Edit Product</span>
            <span className="mono text-sm text-orange font-normal">{product.sku}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs font-medium text-steel-light uppercase tracking-wider">Name</label>
            <Input className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-steel-light uppercase tracking-wider">Short Description</label>
            <textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-md text-sm outline-none focus:border-orange resize-y" />
          </div>
          <div>
            <label className="text-xs font-medium text-steel-light uppercase tracking-wider">Full Description</label>
            <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 w-full px-3 py-2 border border-border rounded-md text-sm outline-none focus:border-orange resize-y font-mono text-xs" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-steel-light uppercase tracking-wider">Brand</label>
              <Input className="mt-1" value={form.brand} onChange={(e) => set("brand", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-steel-light uppercase tracking-wider">Stock Status Label</label>
              <Input className="mt-1" value={form.stock_status} onChange={(e) => set("stock_status", e.target.value)} placeholder="In Stock / Out of Stock" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => set("is_in_stock", !form.is_in_stock)} className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md border transition-colors ${form.is_in_stock ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-border bg-surface text-steel"}`}>
              {form.is_in_stock ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {form.is_in_stock ? "In Stock" : "Out of Stock"}
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-hover text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductsAdmin = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    client.get("/admin/products", { params: { search: search || undefined, page, limit: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const onSaved = (updated) => setData((d) => ({ ...d, items: d.items.map((p) => (p.sku === updated.sku ? updated : p)) }));

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-graphite">Products</h1>
      <p className="text-sm text-steel-light mt-1 mb-5">{data ? `${data.total} products` : "…"} — set a price to show it publicly. Use the eye icon to hide a product from the catalogue without deleting it.</p>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-steel-light" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by SKU, name or brand…" className="pl-9 h-10" />
      </div>

      <div className="border border-border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm min-w-[840px]">
          <thead className="bg-surface text-steel-light">
            <tr className="text-left mono text-xs uppercase tracking-wider">
              <th className="px-4 py-2.5">Product</th>
              <th className="px-4 py-2.5">SKU</th>
              <th className="px-4 py-2.5">Brand</th>
              <th className="px-4 py-2.5">Stock</th>
              <th className="px-4 py-2.5">Price (CAD)</th>
              <th className="px-4 py-2.5 text-center">Visible</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-steel-light" /></td></tr>}
            {!loading && data?.items?.map((p) => (
              <tr key={p.sku} className={`border-t border-border hover:bg-surface/50 ${p.is_visible === false ? "opacity-50" : ""}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded gp-card-img grid place-items-center p-1 shrink-0">
                      {p.main_image ? <img src={p.main_image} alt="" className="max-h-full object-contain mix-blend-multiply" /> : <Box className="w-4 h-4 text-steel-light" />}
                    </div>
                    <span className="text-graphite line-clamp-1 max-w-[200px]">{displayName(p)}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 mono text-xs text-orange">{p.sku}</td>
                <td className="px-4 py-2.5 text-steel">{p.brand || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center gap-1.5 text-xs ${p.is_in_stock ? "text-emerald-600" : "text-steel-light"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.is_in_stock ? "bg-emerald-500" : "bg-steel-light"}`} />
                    {p.is_in_stock ? "In stock" : "Out"}
                  </span>
                </td>
                <td className="px-4 py-2.5"><PriceCell product={p} onSaved={onSaved} /></td>
                <td className="px-4 py-2.5 text-center"><VisibilityToggle product={p} onSaved={onSaved} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(p)} className="p-1.5 rounded text-steel-light hover:text-orange hover:bg-surface" title="Edit product">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {p.original_url && <a href={p.original_url} target="_blank" rel="noreferrer" className="p-1.5 rounded text-steel-light hover:text-orange hover:bg-surface"><ExternalLink className="w-4 h-4" /></a>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="mono text-sm text-steel px-2">{page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {editing && <EditModal product={editing} onClose={() => setEditing(null)} onSaved={(u) => { onSaved(u); }} />}
    </div>
  );
};

export default ProductsAdmin;
